import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../../api';

const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
        return new Date(isoString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return isoString;
    }
};

const authorDisplay = (author) => {
    if (author === 'brandon') return 'Brandon Williams';
    if (author === 'logan') return 'Logan Schmidt (林和正)';
    return author;
};

const PostCard = ({ post }) => {
    const navigate = useNavigate();
    return (
        <button
            onClick={() => navigate(`/blog/post/${post.slug}`)}
            className="text-left bg-slate-950/80 backdrop-blur-sm border border-slate-700 hover:border-amber-500/60 rounded-xl p-5 flex flex-col gap-2 transition-colors w-full"
        >
            <h3 className="text-white font-semibold leading-snug">{post.title}</h3>
            <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{authorDisplay(post.author)}</span>
                {post.publishedAt || post.createdAt ? (
                    <>
                        <span>&middot;</span>
                        <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                    </>
                ) : null}
            </div>
            {post.excerpt && (
                <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
            )}
        </button>
    );
};

const BlogSection = ({ category, title, subtitle, categorySlug }) => {
    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchPosts = async () => {
            setIsLoading(true);
            try {
                const res = await fetch(`${API_URL}/blog?category=${encodeURIComponent(category)}&limit=3`);
                if (!res.ok) {
                    setPosts([]);
                    return;
                }
                const data = await res.json();
                setPosts(data.posts || []);
            } catch {
                setPosts([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPosts();
    }, [category]);

    return (
        <section className="mb-10">
            <div className="bg-slate-900/85 backdrop-blur-md rounded-2xl px-7 pt-6 pb-5 border border-slate-700/50">
                <div className="mb-5">
                    <h2 className="text-2xl font-semibold shimmer-gold mb-1">{title}</h2>
                    <p className="text-gray-400 text-sm italic">{subtitle}</p>
                </div>

                {isLoading && (
                    <div className="flex justify-center py-8">
                        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                    </div>
                )}

                {!isLoading && posts.length === 0 && (
                    <p className="text-gray-500 text-sm italic">
                        Coming soon &mdash; writings will appear here.
                    </p>
                )}

                {!isLoading && posts.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                        {posts.map((post) => (
                            <PostCard key={post.slug || post.id} post={post} />
                        ))}
                    </div>
                )}

                <Link
                    to={`/blog/${categorySlug}`}
                    className="shimmer-gold text-sm font-medium hover:opacity-80 transition-opacity"
                >
                    View all &rarr;
                </Link>
            </div>
        </section>
    );
};

const Blog = () => {
    useEffect(() => {
        document.body.style.backgroundImage = 'url("/images/adminPageBkg.jpg")';
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center';
        document.body.style.backgroundAttachment = 'fixed';
        return () => {
            document.body.style.backgroundImage = 'none';
            document.body.style.backgroundSize = '';
            document.body.style.backgroundPosition = '';
            document.body.style.backgroundAttachment = '';
        };
    }, []);

    return (
        <div className="min-h-screen py-12 px-6" style={{ background: 'rgba(2,6,23,0.45)' }}>
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="mb-10 text-center">
                    <div className="inline-block bg-slate-900/90 backdrop-blur-md rounded-xl px-8 py-6 border border-slate-700/50">
                        <h1 className="text-4xl font-bold shimmer-gold mb-2">Buddhist Journal</h1>
                        <p className="text-gray-300 text-sm italic">
                            A shared record of practice, study, and the path toward ordination
                        </p>
                    </div>
                </div>

                <BlogSection
                    category="path-to-the-robe"
                    categorySlug="path-to-the-robe"
                    title="The Path to the Robe"
                    subtitle="Brandon and Logan's ordination journey — two lay practitioners, aspirant bhik&#7779;us"
                />

                <BlogSection
                    category="as-it-was-taught-to-me"
                    categorySlug="as-it-was-taught-to-me"
                    title="As It Was Taught to Me"
                    subtitle="Brandon and Logan's dharma teaching notes — absorption practices, methods, transmissions received"
                />
            </div>
        </div>
    );
};

export default Blog;
